import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../../ForListPage.scss';
import '../../../recruit/common/RecruitList.scss';
import '../ministerInfo/MinisterInfo.scss';
import './MinisterPageList.scss';
import MainURL from '../../../../MainURL';
import Pagination from '../../../../components/Pagination';
import Loading from '../../../../components/Loading';
import ScrollToTopButton from '../../../../components/ScrollToTopButton';
import { copyText, profilePhotoSrc, profileShareUrl, profileSubTitle } from './ministerPageUtils';
import type { MinisterProfileListItem } from './ministerPageUtils';

const PAGE_SIZE = 12; // 4·3·2열 모두 나누어떨어짐

// 검색 대상 — 이름·직분·구분·교단·교회·담당 사역
const searchText = (item: MinisterProfileListItem) =>
  [
    item.name,
    item.dutyTitle,
    item.sort,
    item.religiousbody,
    item.church,
    ...item.ministryFields,
  ].join(' ').toLowerCase();

// 프로필페이지 목록 — 공개한 프로필페이지 (상세: /minister/profilepage/detail)
export default function MinisterPageList() {
  const navigate = useNavigate();

  const [allList, setAllList] = useState<MinisterProfileListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchWord, setSearchWord] = useState('');
  const [query, setQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [copiedId, setCopiedId] = useState('');

  useEffect(() => {
    let cancelled = false;
    axios.get(`${MainURL}/ministerprofile/list`)
      .then((res) => {
        if (cancelled) return;
        setAllList(Array.isArray(res.data?.list) ? res.data.list : []);
      })
      .catch((err) => {
        console.error('프로필페이지 목록 조회 실패:', err);
        if (!cancelled) setAllList([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const word = query.toLowerCase();
    return word ? allList.filter((item) => searchText(item).includes(word)) : allList;
  }, [allList, query]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageList = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSearch = () => {
    setCurrentPage(1);
    setQuery(searchWord.trim());
  };

  const handleReset = () => {
    setSearchWord('');
    setCurrentPage(1);
    setQuery('');
  };

  const openDetail = (id: string) => {
    navigate(`/minister/profilepage/detail?id=${id}`);
    window.scrollTo(0, 0);
  };

  const copyLink = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    await copyText(profileShareUrl(id));
    setCopiedId(id);
    setTimeout(() => setCopiedId((cur) => (cur === id ? '' : cur)), 1500);
  };

  return (
    <div className="recruit minister-info">
      <div className="inner">
        <div className="subpage__main">
          <div className="subpage__main__title">
            <h3>프로필</h3>
          </div>

          <div className="subpage__main__search">
            <input
              className="inputdefault width"
              type="text"
              placeholder="이름, 교회, 교단, 담당 사역으로 검색"
              value={searchWord}
              onChange={(e) => setSearchWord(e.target.value)}
              style={{ margin: '0', outline: 'none', paddingLeft: '10px', border: '1px solid #ccc' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
            />
            <div className="buttons">
              <div className="btn search" onClick={handleSearch}>
                <p>검색</p>
              </div>
              <div className="btn reset" onClick={handleReset}>
                <p>초기화</p>
              </div>
            </div>
          </div>

          <div className="subpage__main__content">
            <p className="minister-info__count">총 <b>{filtered.length}</b>명</p>
            {isLoading ? (
              <div className="list-loading">
                <Loading />
              </div>
            ) : pageList.length === 0 ? (
              <div className="minister-info__empty">
                <p>{query ? '검색 결과가 없습니다.' : '등록된 프로필이 없습니다.'}</p>
              </div>
            ) : (
              <div className="profile-list__grid">
                {pageList.map((item) => {
                  const imageSrc = profilePhotoSrc(item);
                  return (
                    <div key={item.id} className="profile-list__card" onClick={() => openDetail(item.id)}>
                      <div className="profile-list__photo">
                        {imageSrc ? (
                          <img src={imageSrc} alt={item.name} draggable={false} />
                        ) : (
                          <span>{item.name.slice(0, 1)}</span>
                        )}
                      </div>

                      <div className="profile-list__info">
                        <p className="profile-list__name">{item.name}</p>
                        <p className="profile-list__meta">{profileSubTitle(item) || '-'}</p>

                        <dl className="profile-list__rows">
                          <div>
                            <dt>교회</dt>
                            <dd>{item.church || '-'}</dd>
                          </div>
                          <div>
                            <dt>교단</dt>
                            <dd>{item.religiousbody || '-'}</dd>
                          </div>
                          <div>
                            <dt>사역</dt>
                            <dd>{item.ministryFields.join(', ') || '-'}</dd>
                          </div>
                        </dl>

                        <button
                          type="button"
                          className={`profile-list__link${copiedId === item.id ? ' is-copied' : ''}`}
                          onClick={(e) => copyLink(e, item.id)}
                        >
                          {copiedId === item.id ? '링크가 복사되었습니다' : '링크 복사'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              onAfterChange={() => window.scrollTo(0, 0)}
            />
          </div>
        </div>
      </div>
      <ScrollToTopButton />
    </div>
  );
}
